import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, FileCheckIcon } from 'lucide-react';
export function DefensibleTechSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const features = [{
    icon: ShieldCheckIcon,
    title: 'Command-Grade Security',
    description: 'Built to meet the highest operational security standards.'
  }, {
    icon: FileCheckIcon,
    title: 'Compliance-Ready Architecture',
    description: 'Designed for environments where traceability and accountability are mandatory.'
  }, {
    icon: LockIcon,
    title: 'Explainable Readiness Intelligence',
    description: 'Every readiness score is transparent and backed by structured signals.'
  }];
  return <section ref={ref} className="w-full bg-slate-950 py-24 lg:py-32 relative overflow-hidden" data-id="element-126">
      {/* Subtle purple glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-tiq-purple/10 rounded-full blur-[150px]" data-id="element-127" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10" data-id="element-128">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-129">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-tiq-purple/30 bg-tiq-purple/10 mb-8" data-id="element-130">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-tiq-purple" data-id="element-131" />
            <span className="text-xs font-mono text-tiq-purple uppercase tracking-wider" data-id="element-132">
              Enterprise-Grade
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6 max-w-4xl mx-auto" data-id="element-133">
            Built for Environments Where Decisions Must Be Defensible
          </h2>

          <p className="text-xl text-slate-300 max-w-3xl mx-auto" data-id="element-134">
            TalentIQ was designed for organizations where operational decisions
            must be explainable, auditable, and trusted.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-135">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <motion.div key={feature.title} className="relative bg-slate-900/50 border border-slate-800 rounded-xl p-8 hover:border-tiq-purple/30 transition-colors duration-300" initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.3 + i * 0.15
          }} data-id="element-136">
                <div className="w-12 h-12 rounded-lg bg-tiq-purple/10 border border-tiq-purple/20 flex items-center justify-center mb-6" data-id="element-137">
                  <Icon className="w-6 h-6 text-tiq-purple" data-id="element-138" />
                </div>

                <h3 className="text-xl font-bold text-white mb-3" data-id="element-139">
                  {feature.title}
                </h3>

                <p className="text-slate-400 leading-relaxed" data-id="element-140">
                  {feature.description}
                </p>

                {/* Subtle glow on hover */}
                <div className="absolute inset-0 bg-tiq-purple/5 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-300 -z-10" data-id="element-141" />
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.9
      }} className="text-center mt-16" data-id="element-142">
          <div className="inline-block px-6 py-3 bg-slate-900/50 border border-slate-800 rounded-lg mb-16" data-id="element-143">
            <p className="text-sm text-slate-400" data-id="element-144">
              <span className="text-tiq-green font-semibold" data-id="element-145">
                Trusted by organizations
              </span>{' '}
              where operational integrity isn't optional.
            </p>
          </div>

          {/* Video Player */}
          <div className="relative w-full max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-black border border-slate-800" data-id="element-146">
            <video src="https://cdn.magicpatterns.com/uploads/p9xp65CcbAesHsnn7yiMo4/TIQ 2026 Pitch Deck (1).mp4" controls className="w-full h-auto" poster="https://cdn.magicpatterns.com/uploads/63ibHPLhxEGMfaRuduQviG/TalentIQ_Investor_Deck_26.png" data-id="element-147">
              Your browser does not support the video tag.
            </video>
          </div>
        </motion.div>
      </div>
    </section>;
}